import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ChevronDown, FileStack, Notebook } from 'lucide-react'
import { buildIconPath } from '@/lib/imagePaths'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useTimezoneDate } from '@/hooks/useTimezoneDate'
import { UserNameWithPresence } from '@/components/ui/UserNameWithPresence'

export interface RecoveryHeaderChildObject {
    id: string
    name: string
    status: 'complete' | 'in-progress' | 'pending'
}

export interface RecoveryHeaderUpdate {
    id: string
    action: string
    dateTime: Date
    username: string
    userId?: number | null
    message: string
}

export interface RecoveryTrackingHeaderProps {
    objectId: string
    description: string
    iconFilename?: string | null
    vendorLogo?: string
    vendorName: string
    assigned: Date
    plannedFinish: Date
    forecastFinish: Date
    elapsed: string
    timeRemaining: string
    isOverdue: boolean
    engineerRto: string
    rta: string
    nextUpdateDue: string
    isNextUpdateOverdue?: boolean
    requestObjectCount?: number
    childObjects: RecoveryHeaderChildObject[]
    updates: RecoveryHeaderUpdate[]
    onAddUpdate?: () => void
    onViewChildObjects: () => void
}

/** Map raw action string to i18n key */
const ACTION_I18N_MAP: Record<string, string> = {
    'Assigned': 'recoverRequests.actionAssigned',
    'Started': 'recoverRequests.actionStarted',
    'Unassigned': 'recoverRequests.actionUnassigned',
    'InProgress': 'recoverRequests.actionInProgress',
    'In Progress': 'recoverRequests.actionInProgress',
    'Completed': 'recoverRequests.actionCompleted',
    'Issue': 'recoverRequests.actionIssue',
}

function formatDateFallback(date: Date): string {
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

/** Dot colour by recovery update action/status: Assigned, Started, Unassigned, InProgress, Completed, Issue */
export function getStatusDotClass(action: string): string {
    const a = (action || '').trim()
    if (a === 'Assigned') return 'bg-emerald-500'
    if (a === 'Started') return 'bg-blue-500'
    if (a === 'Unassigned') return 'bg-gray-400'
    if (a === 'InProgress' || a === 'In Progress') return 'bg-amber-500'
    if (a === 'Completed') return 'bg-teal-500'
    if (a === 'Issue') return 'bg-red-500'
    return 'bg-gray-400'
}

/** Pill/badge classes for status text (same colours as dot): Assigned, Started, Unassigned, InProgress, Completed, Issue */
export function getStatusPillClass(action: string): string {
    const a = (action || '').trim()
    if (a === 'Assigned') return 'bg-emerald-100 text-emerald-700'
    if (a === 'Started') return 'bg-blue-100 text-blue-700'
    if (a === 'Unassigned') return 'bg-gray-100 text-gray-700'
    if (a === 'InProgress' || a === 'In Progress') return 'bg-amber-100 text-amber-700'
    if (a === 'Completed') return 'bg-teal-100 text-teal-700'
    if (a === 'Issue') return 'bg-red-100 text-red-700'
    return 'bg-gray-100 text-gray-600'
}

export function RecoveryTrackingHeader(props: RecoveryTrackingHeaderProps) {
    const {
        objectId,
        description,
        iconFilename,
        vendorLogo,
        vendorName,
        assigned,
        plannedFinish,
        forecastFinish,
        elapsed,
        timeRemaining,
        isOverdue,
        engineerRto,
        rta,
        nextUpdateDue,
        isNextUpdateOverdue = false,
        requestObjectCount,
        childObjects,
        updates,
        onAddUpdate,
        onViewChildObjects,
    } = props
    const { t } = useTranslation()
    const { formatDateShort: tzFormatDate } = useTimezoneDate()
    const formatDateShort = (date: Date) => {
        try { return tzFormatDate(date) } catch { return formatDateFallback(date) }
    }
    const [updatesExpanded, setUpdatesExpanded] = useState(true)
    const [selectedUpdate, setSelectedUpdate] = useState<RecoveryHeaderUpdate | null>(null)

    const vendorAbbrev = vendorName
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 5)

    const objectCount = requestObjectCount ?? childObjects.length

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="flex">
                <div className="flex-1 min-w-0">
                    <div className="px-4 py-3 flex items-center gap-3 min-w-0 border-b border-gray-200">
                        {iconFilename && (
                            <img src={buildIconPath(iconFilename)} alt="" className="h-8 w-8 object-contain shrink-0" />
                        )}
                        <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{objectId}</p>
                            {description ? <p className="text-sm text-gray-500 truncate">{description}</p> : null}
                        </div>
                    </div>

                    <div className="px-4 py-3 flex flex-wrap items-center gap-6 border-b border-gray-200">
                        <div>
                            <p className="text-xs text-muted-foreground">{t('recoverRequests.engineerRtoLabel')}</p>
                            <p className="text-sm font-semibold text-gray-900">{engineerRto}</p>
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{t('recoverRequests.rtaLabel')}</p>
                            <p className="text-sm font-semibold text-gray-900">{rta}</p>
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{t('recoverRequests.elapsedLabel')}</p>
                            <p className="text-sm font-semibold text-gray-900">{elapsed}</p>
                        </div>
                        <button type="button" onClick={onViewChildObjects} className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 transition-colors" aria-label="View child objects">
                            <FileStack className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">{t('recoverRequests.objectsCount', { count: objectCount })}</span>
                        </button>
                    </div>

                    <div className="border-b border-gray-200">
                        <div className="px-4 py-3 flex items-center justify-between gap-3">
                            <button type="button" className="flex items-center gap-2 text-left hover:bg-gray-50 -m-2 px-2 py-1.5 rounded transition-colors" onClick={() => setUpdatesExpanded((v) => !v)} aria-expanded={updatesExpanded}>
                                <Notebook className="h-4 w-4 text-gray-500 shrink-0" aria-hidden="true" />
                                <span className="text-xs font-semibold text-gray-500 uppercase">{t('recoverRequests.recoveryUpdates')} ({updates.length})</span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform shrink-0 ${updatesExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                            </button>
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="bg-white border border-gray-200 px-3 py-1.5 rounded-lg">
                                    <span className="text-xs text-muted-foreground">{t('recoverRequests.nextUpdateLabel') || 'Next Update:'}</span>{' '}
                                    <span className={`font-bold ${isNextUpdateOverdue ? 'text-red-600' : 'text-green-600'}`}>{nextUpdateDue}</span>
                                </div>
                                {onAddUpdate && (
                                    <Button size="sm" className="px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={onAddUpdate} aria-label={t('recoverRequests.addUpdateButton')}>
                                        {t('recoverRequests.addUpdateButton')}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {updatesExpanded && updates.length > 0 && (
                            <div className="px-4 pb-3">
                                <div className="overflow-x-auto rounded border border-gray-200">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 w-8" scope="col">{t('recoverRequests.status')}</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">{t('recoverRequests.action')}</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap w-36" scope="col">{t('recoverRequests.dateAndTime')}</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">{t('recoverRequests.userLabel')}</th>
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 min-w-[8rem]" scope="col">{t('recoverRequests.engineerUpdate')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {updates.map((u) => {
                                                const dotClass = getStatusDotClass(u.action)
                                                const pillClass = getStatusPillClass(u.action)
                                                return (
                                                    <tr
                                                        key={u.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => setSelectedUpdate(u)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault()
                                                                setSelectedUpdate(u)
                                                            }
                                                        }}
                                                        className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 cursor-pointer"
                                                        aria-label={`View update: ${u.action} at ${formatDateShort(u.dateTime)}`}
                                                    >
                                                        <td className="py-2 px-3 align-middle">
                                                            <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`} title={`Action: ${u.action}`} aria-hidden="true" />
                                                        </td>
                                                        <td className="py-2 px-3">
                                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${pillClass}`}>{ACTION_I18N_MAP[u.action] ? t(ACTION_I18N_MAP[u.action]) : (u.action || '—')}</span>
                                                        </td>
                                                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{formatDateShort(u.dateTime)}</td>
                                                        <td className="py-2 px-3 text-gray-600">{u.userId ? <UserNameWithPresence userId={u.userId} name={u.username || '—'} /> : (u.username || '—')}</td>
                                                        <td className="py-2 px-3 text-gray-700">
                                                            {u.message ? (
                                                                <div
                                                                    className="prose prose-sm max-w-none [&_ul]:list-disc [&_ol]:list-decimal [&_p]:mb-1 [&_p:last-child]:mb-0"
                                                                    dangerouslySetInnerHTML={{ __html: u.message }}
                                                                />
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {updatesExpanded && updates.length === 0 && <div className="px-4 pb-3 text-sm text-muted-foreground">{t('recoverRequests.noRecoveryUpdatesYet')}</div>}
                    </div>
                </div>

                <div className="w-52 shrink-0 bg-gray-50 border-l border-gray-200 p-4 relative" aria-label="Recovery timeline">
                    <div className="absolute left-2 top-4 bottom-4 w-0.5 bg-gray-300" aria-hidden="true" />
                    <div className="relative z-10 space-y-6">
                        <div className="flex items-start gap-3">
                            <div className="w-4 h-4 rounded-full border-2 border-white shadow bg-emerald-500 shrink-0 mt-0.5" aria-label={t('recoverRequests.assignedLabel')} />
                            <div>
                                <p className="text-xs text-gray-500">{t('recoverRequests.assignedLabel')}</p>
                                <p className="text-sm font-medium text-gray-900">{formatDateShort(assigned)}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-4 h-4 rounded-full border-2 border-white shadow bg-amber-400 shrink-0 mt-0.5" aria-label={t('recoverRequests.plannedFinishLabel')} />
                            <div>
                                <p className="text-xs text-gray-500">{t('recoverRequests.plannedFinishLabel')}</p>
                                <p className="text-sm font-medium text-gray-900">{formatDateShort(plannedFinish)}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 border-white shadow shrink-0 mt-0.5 ${isOverdue ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} aria-label={t('recoverRequests.timeRemainingLabel') || 'Time Remaining'} />
                            <div>
                                <p className="text-xs text-gray-500">{t('recoverRequests.timeRemainingLabel') || 'Time Remaining'}</p>
                                <p className={`text-sm font-bold ${isOverdue ? 'text-red-600' : 'text-emerald-600'}`}>{timeRemaining}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-4 h-4 rounded-full border-2 border-white shadow bg-gray-400 shrink-0 mt-0.5" aria-label={t('recoverRequests.forecastFinishLabel')} />
                            <div>
                                <p className="text-xs text-gray-500">{t('recoverRequests.forecastFinishLabel')}</p>
                                <p className="text-sm font-medium text-gray-900">{formatDateShort(forecastFinish)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={!!selectedUpdate} onOpenChange={(open) => !open && setSelectedUpdate(null)}>
                <DialogContent className="sm:max-w-md" aria-describedby={selectedUpdate ? 'recovery-update-detail-description' : undefined}>
                    {selectedUpdate && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{t('recoverRequests.recoveryUpdates')}</DialogTitle>
                            </DialogHeader>
                            <div id="recovery-update-detail-description" className="space-y-4 pt-2">
                                <div className="flex items-center gap-3">
                                    <span className={`inline-block w-3 h-3 rounded-full shrink-0 ${getStatusDotClass(selectedUpdate.action)}`} aria-hidden="true" />
                                    <span className={`inline-flex px-2 py-0.5 rounded text-sm font-medium ${getStatusPillClass(selectedUpdate.action)}`}>
                                        {ACTION_I18N_MAP[selectedUpdate.action] ? t(ACTION_I18N_MAP[selectedUpdate.action]) : (selectedUpdate.action || '—')}
                                    </span>
                                </div>
                                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                                    <dt className="text-muted-foreground">{t('recoverRequests.dateAndTime')}</dt>
                                    <dd className="text-gray-900">{formatDateShort(selectedUpdate.dateTime)}</dd>
                                    <dt className="text-muted-foreground">{t('recoverRequests.userLabel')}</dt>
                                    <dd className="text-gray-900">{selectedUpdate.userId ? <UserNameWithPresence userId={selectedUpdate.userId} name={selectedUpdate.username || '—'} /> : (selectedUpdate.username || '—')}</dd>
                                    <dt className="text-muted-foreground align-top pt-1">{t('recoverRequests.engineerUpdate')}</dt>
                                    <dd className="text-gray-900">
                                        {selectedUpdate.message ? (
                                            <div
                                                className="prose prose-sm max-w-none [&_ul]:list-disc [&_ol]:list-decimal [&_p]:mb-1 [&_p:last-child]:mb-0 break-words"
                                                dangerouslySetInnerHTML={{ __html: selectedUpdate.message }}
                                            />
                                        ) : (
                                            '—'
                                        )}
                                    </dd>
                                </dl>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
